import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { DotPattern } from '@/components/ui/dot-pattern'
import { ThemeToggle } from '@/components/theme-toggle'
import { Logo } from '@/components/ui/logo'
import { cn } from '@/lib/utils'
import { Lock, ArrowRight, Loader2, AlertCircle } from 'lucide-react'
import { TestAuthService } from '@/services/test-auth-service'

interface TestLoginProps {
  onSuccess: () => void
}

export function TestLogin({ onSuccess }: TestLoginProps) {
  const [token, setToken] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)

    const current = token.trim()
    if (!current) {
      setError('Informe o token de acesso.')
      return
    }

    setLoading(true)
    try {
      const result = await TestAuthService.verificarToken(current)
      if (!result.success) {
        const code = (result.error || '').toUpperCase()
        if (code === 'TOKEN_NOT_CONFIGURED') {
          setError('Token não configurado no ambiente.')
        } else if (code === 'NETWORK_ERROR') {
          setError('Falha de conexão. Tente novamente.')
        } else {
          setError('Token inválido. Verifique e tente novamente.')
        }
        return
      }

      TestAuthService.salvarAcesso()
      onSuccess()
    } finally {
      setLoading(false)
    }
  }

  const disabled = loading || token.trim().length === 0

  return (
    <div className="min-h-screen flex items-center justify-center p-6 lg:p-8 relative overflow-hidden bg-gradient-to-br from-slate-50 via-sky-50/40 to-indigo-50/40 dark:from-background dark:via-background dark:to-background">
      <DotPattern
        className={cn(
          '[mask-image:radial-gradient(500px_circle_at_center,white,transparent)]',
          'fixed inset-0 h-full w-full',
          'text-slate-300/40 dark:text-slate-600/60'
        )}
      />

      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>

      <div className="w-full max-w-md relative z-10">
        <Card className="backdrop-blur-md bg-white/95 dark:bg-background/95 border border-slate-200 dark:border-border/40 shadow-xl">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto h-16">
              <Logo className="h-16" />
            </div>
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold text-foreground">Acesso de Testes</h1>
              <CardDescription>
                Área restrita para validação interna. Informe o token compartilhado com a equipe.
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="test-token" className="text-sm font-medium text-foreground flex items-center gap-2">
                  Token de acesso
                  <Lock className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                </label>
                <Input
                  id="test-token"
                  type="password"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="Digite o token fornecido"
                  value={token}
                  onChange={(event) => setToken(event.target.value)}
                  disabled={loading}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Este token é rotacionado manualmente e protegido por variáveis de ambiente.
                </p>
              </div>

              {error && (
                <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
                  <AlertCircle className="mt-0.5 h-4 w-4" aria-hidden="true" />
                  <span>{error}</span>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={disabled} aria-busy={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    Validando token...
                  </>
                ) : (
                  <>
                    Entrar
                    <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
